import { useEffect, useMemo, useState } from 'react';
// import { useBankAccounts } from 'hooks/useBankAccounts';
import {
  AutoComplete,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  message,
  Space,
  Table,
  Typography,
} from 'antd';
import { REQUEST_LOADING } from 'constants/alerts';
import { MX_PAGE_ACTION_EVENT } from 'constants/mixpanelEvents';
import { CREATE_OPERATION, UPDATE_OPERATION } from 'constants/operations';
import { STATUS_ERROR, STATUS_SUCCESS } from 'constants/responseStatuses';
import { COLUMN_MEDIUM, COLUMN_SMALL } from 'constants/table';
import dayjs from 'dayjs';
import { useMe } from 'hooks/useMe';
import { useMixpanel } from 'hooks/useMixpanel';
import { ContractorsService } from 'services/ContractorsService';
import { DocumentsService } from 'services/DocumentsService';
import { FormSection } from 'ui/FormSection';
import { FormWrapper } from 'ui/FormWrapper';
import { formatDate } from 'utils';
import { getUserFullname } from 'utils/auth';
import {
  iinRule,
  onlyNumberRule,
  requiredRule,
} from 'utils/forms/validationRules';
import { docCategories } from 'utils/handbooks/documentsHandbooks';
import { priceRender } from 'utils/table';
import { FormItemsInTable } from './style';

function ReconciliationStatementForm({
  initialValues,
  clientId,
  clientInfo,
  copyDocId,
  onConfirm,
  isActive,
  onSuccess,
}) {
  const { trackEvent } = useMixpanel();
  const user = useMe();
  const [form] = Form.useForm();
  const docId = initialValues?.id;

  const [loading, setLoading] = useState(false);
  const [lengthIin, setLengthIin] = useState(0);
  const [contractors, setContractors] = useState([]);
  const [contractsContr, setContractsContr] = useState([]);
  const [contrId, setContrId] = useState();
  const [beginDate, setBeginDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [type, setType] = useState(null);
  const [data, setData] = useState();
  const [nonSelected, setNonSelected] = useState([]);

  const disabledBeginDate = useMemo(() => {
    return (current) => (endDate ? current && current > dayjs(endDate) : false);
  }, [endDate]);

  const disabledEndDate = useMemo(() => {
    return (current) =>
      beginDate ? current && current < dayjs(beginDate) : false;
  }, [beginDate]);

  const getClientsContractor = async () => {
    try {
      const res = await ContractorsService.getClientsContractors(clientId);
      if (res) setContractors(res);
    } catch (error) {
      message.error('Не удалось подтянуть контрагентов');
    }
  };

  const getContractorContracts = async () => {
    try {
      const res = await ContractorsService.getClientsContractContr(
        clientId,
        contrId
      );
      if (res) setContractsContr(res);
    } catch (error) {
      message.error('Не удалось получить договора контрагента');
    }
  };

  useEffect(() => {
    if (contrId) getContractorContracts();
  }, [contrId]);

  useEffect(() => {
    if (initialValues) {
      fetchDocumentDetails();
    }
  }, []);

  useEffect(() => {
    if (clientId) getClientsContractor();
  }, [clientId, isActive]);

  const fetchDocumentDetails = async () => {
    const {
      doc_id,
      acc_id,
      contractor_bin_iin,
      buyer_name,
      buyer_address,
      document_date,
      number_contract,
      executor_position,
      executor_full_name,
      date_contract,
      include_seal,
    } = await DocumentsService.getInvoicePayment(clientId, docId || copyDocId);

    const formValues = {
      doc_id,
      acc_id,
      contractor_bin_iin,
      buyer_name,
      buyer_address,
      number_contract,
      executor_position,
      executor_full_name,
      date_contract: date_contract ? dayjs(date_contract) : undefined,
      document_date: dayjs(document_date),
      include_seal,
    };

    if (copyDocId) {
      delete formValues.doc_id;
      delete formValues.document_date;
    }

    form.setFieldsValue(formValues);
  };

  const createReconcilliationStatemnet = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);
    setLoading(true);

    try {
      const resData = await DocumentsService.createReconcilliationStatemnet(
        payload
      );

      if (resData) {
        message.success('Успешно сформировано');
        setData(resData);
      }
    } catch (err) {
      message.error('Не удалось сформировать');
    } finally {
      hide();
      setLoading(false);
    }
  };

  const createReconcilliationStatemnetFinal = async (
    payload,
    mixpanelEventPayload
  ) => {
    let responseStatus;

    if (!data) {
      message.warning('Сначала сформируйте!');
      return;
    }
    const hide = message.loading(REQUEST_LOADING);

    setLoading(true);

    try {
      const createdRecord =
        await DocumentsService.createReconcilliationStatemnetFinal({
          ...payload,
          form: data.form,
          array: data.array.filter((value) => !nonSelected.includes(value.id)),
        });

      if (createdRecord) {
        responseStatus = STATUS_SUCCESS;
        setLoading(false);
        onConfirm(createdRecord);
        onSuccess();
        message.success('Документ успешно создан');
        onClose();
        form.resetFields();
      }
    } catch (err) {
      responseStatus = STATUS_ERROR;
      setLoading(false);
      message.error('Не удалось создать документ');
    } finally {
      hide();
      trackEvent(MX_PAGE_ACTION_EVENT, {
        ...mixpanelEventPayload,
        responseStatus,
      });
    }
  };

  const onClose = () => {
    setData(null);
    setType(null);
  };

  const handleSubmitByStatus = (status) => () => {
    setType(status);
    form.submit();
  };

  const handleSubmit = async (data) => {
    const payload = {
      ...data,
      document_date: formatDate(data?.document_date, 'YYYY-MM-DD'),
      begin_date: formatDate(data?.begin_date, 'YYYY-MM-DD'),
      end_date: formatDate(data?.end_date, 'YYYY-MM-DD'),
      date_contract: data?.date_contract
        ? formatDate(data?.date_contract, 'YYYY-MM-DD')
        : undefined,
      client_id: clientId,
    };

    delete payload.date;
    delete payload.txtfile;

    const mixpanelEventPayload = {
      user: getUserFullname(user),
      client: clientInfo?.full_name,
      operation: initialValues ? UPDATE_OPERATION : CREATE_OPERATION,
      docType: docCategories.reconciliation_statement,
      page: 'Первичные документы',
    };

    if (type === 'pre') {
      createReconcilliationStatemnet(payload, mixpanelEventPayload);
    } else {
      createReconcilliationStatemnetFinal(payload, mixpanelEventPayload);
    }
  };

  const handleSelect = (e, id) => {
    const { checked } = e.target;

    setNonSelected((prev) => {
      if (!checked) {
        return [...prev, id];
      }
      return prev.filter((value) => value !== id);
    });
  };

  const handleContractorAutocomplete = async () => {
    const iin = form.getFieldValue('contractor_bin_iin');
    if (iin.length === 12 && lengthIin === 11) {
      setContrId(null);
      setContractsContr([]);
      const hide = message.loading('Загрузка данных контрагента...');
      try {
        const data = await ContractorsService.autoCompleteContractor(iin);

        if (data) {
          const {
            nameRu = '',
            addressRu = '',
            firstNameRu = '',
            lastNameRu = '',
            middleNameRu = '',
          } = data;
          form.setFieldsValue({
            buyer_name:
              nameRu === ''
                ? `${lastNameRu} ${firstNameRu} ${middleNameRu}`
                : nameRu,
            buyer_address: addressRu,
          });
        } else {
          message.warning('Нет данных о контрагенте');
        }
      } catch (err) {
        message.error('Ошибка сервиса ЭСФ');
      } finally {
        hide();
      }
    }
    setLengthIin(form.getFieldValue('contractor_bin_iin')?.length);
  };

  return (
    <FormWrapper>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelAlign="right"
      >
        <FormSection id="ssss">
          <FormItemsInTable>
            <div className="table">
              <div className="title">
                Период <span style={{ color: 'red' }}>*</span>
              </div>
              <div />
              <div className="title">
                Контрагент <span style={{ color: 'red' }}>*</span>
              </div>
              <div />
              <div className="title">Договор </div>
              <div />
              <div />
              <Form.Item
                style={{ width: '100%' }}
                name="begin_date"
                rules={[requiredRule]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  allowClear
                  style={{ width: '100%' }}
                  disabledDate={disabledBeginDate}
                  onChange={(date) => setBeginDate(date)}
                />
              </Form.Item>
              <Form.Item
                style={{ width: '100%' }}
                name="end_date"
                rules={[requiredRule]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  allowClear
                  style={{ width: '100%' }}
                  disabledDate={disabledEndDate}
                  onChange={(date) => setEndDate(date)}
                />
              </Form.Item>
              <Form.Item
                name="contractor_bin_iin"
                rules={[requiredRule, onlyNumberRule, iinRule]}
              >
                <AutoComplete
                  options={
                    contractors?.length > 0
                      ? contractors?.map((i) => {
                          return {
                            value: i?.bin_iin,
                            label: (
                              <>
                                <div>{i.bin_iin}</div>
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: '13px' }}
                                >
                                  {i.nameRu} / {i.addressRu}
                                </Typography.Text>
                              </>
                            ),
                            ...i,
                          };
                        })
                      : []
                  }
                  onSelect={(_, o) => {
                    form.setFieldsValue({
                      contractor_bin_iin: o.bin_iin,
                      contractor_name: o.nameRu,
                      contractor_address: o.addressRu,
                    });
                    setContrId(o.id);
                  }}
                  filterOption={(inputValue, option) =>
                    option.value
                      .toUpperCase()
                      .indexOf(inputValue.toUpperCase()) !== -1
                  }
                  allowClear
                  maxLength={12}
                  onChange={handleContractorAutocomplete}
                  placeholder="Введите ИИН/БИН"
                />
              </Form.Item>
              <Form.Item name="contractor_name" rules={[requiredRule]}>
                <Input placeholder="Введите наименование" allowClear />
              </Form.Item>
              <Form.Item name="number_contract">
                <AutoComplete
                  options={
                    contractsContr?.length > 0
                      ? contractsContr?.map((i) => {
                          return {
                            value: i?.contract_number,
                            label: (
                              <>
                                <div>{i.contract_number}</div>
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: '13px' }}
                                >
                                  {dayjs(i.contract_date).format('DD.MM.YYYY')}
                                </Typography.Text>
                              </>
                            ),
                            ...i,
                          };
                        })
                      : []
                  }
                  placeholder="Введите номер договора"
                  allowClear
                  filterOption={(inputValue, option) =>
                    option.value
                      .toUpperCase()
                      .indexOf(inputValue.toUpperCase()) !== -1
                  }
                  onSelect={(_, o) => {
                    form.setFieldsValue({
                      number_contract: o.contract_number,
                      date_contract: dayjs(o.contract_date),
                    });
                  }}
                />
              </Form.Item>
              <Form.Item name="date_contract">
                <DatePicker
                  format="DD.MM.YYYY"
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    disabled={loading}
                    loading={loading}
                    type="text"
                    onClick={handleSubmitByStatus('pre')}
                    className="btn_submit"
                  >
                    Cформировать
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </FormItemsInTable>
        </FormSection>
      </Form>
      <Table
        dataSource={data?.array}
        columns={[
          { title: '№', dataIndex: 'id', key: 'id', width: 50 },
          {
            width: COLUMN_MEDIUM,
            title: 'Дата cоздания',
            dataIndex: 'document_date',
            key: 'document_date',
          },
          {
            width: COLUMN_MEDIUM,
            title: 'Документ',
            dataIndex: 'filename',
            key: 'filename',
            render: (value) => {
              return Array.isArray(value) ? value.join(', ') : value;
            },
          },
          {
            width: COLUMN_SMALL,
            title: 'Сумма',
            dataIndex: 'debet',
            key: 'debet',
            render: (sum, i) =>
              sum ? (
                <div style={{ color: '#52C41A' }}>{`+${priceRender(
                  +sum
                )}`}</div>
              ) : (
                <div style={{ color: '#FF4D4F' }}>{`-${priceRender(
                  +i.credit
                )}`}</div>
              ),
          },
          {
            width: 50,
            title: 'Действия',
            dataIndex: 'id',
            render: (id) => {
              return (
                <Checkbox
                  defaultChecked
                  onChange={(e) => handleSelect(e, id)}
                />
              );
            },
          },
        ]}
        style={{ minWidth: '1200px' }}
        pagination={false}
      />

      <Button
        type="primary"
        style={{ margin: '10px 0' }}
        onClick={handleSubmitByStatus('final')}
      >
        Создать
      </Button>
    </FormWrapper>
  );
}

export { ReconciliationStatementForm };
